<template>
  <div class="mini-decoration-page">
    <el-card shadow="hover">
      <template slot="header">
        <div class="card-header">
          <span>小程序装修</span>
          <el-button class="btn-primary" :loading="saving" @click="saveConfig">保存配置</el-button>
        </div>
      </template>

      <div class="decoration-layout">
        <aside class="decoration-aside">
          <div class="aside-caption">装修模块</div>
          <el-menu
            :default-active="activeSection"
            class="decoration-side-menu"
            unique-opened
            background-color="transparent"
            @select="onSectionSelect"
          >
            <el-submenu index="grp-copy">
              <template slot="title">
                <span class="submenu-title">
                  <i class="el-icon-edit-outline"></i>
                  文案与公告
                </span>
              </template>
              <el-menu-item index="announcement">
                <i class="el-icon-bell"></i>
                <span>公告发布</span>
              </el-menu-item>
            </el-submenu>
            <el-submenu index="grp-images">
              <template slot="title">
                <span class="submenu-title">
                  <i class="el-icon-picture"></i>
                  图片素材
                </span>
              </template>
              <el-menu-item index="banner">
                <i class="el-icon-picture-outline"></i>
                <span>首页轮播图</span>
              </el-menu-item>
              <el-menu-item index="footer">
                <i class="el-icon-crop"></i>
                <span>首页底部图片</span>
              </el-menu-item>
            </el-submenu>
          </el-menu>
        </aside>

        <div class="decoration-main">
          <div v-show="activeSection === 'announcement'" class="section-block">
            <div class="section-title">公告发布</div>
            <p class="section-hint">配置小程序首页顶部公告条；可选在每次打开小程序时以弹窗形式展示（同一次启动仅弹一次）。</p>
            <el-form label-width="160px" size="small" class="announce-form">
              <el-form-item label="顶部公告栏">
                <el-switch v-model="form.announcement_bar_enabled" active-text="开启" inactive-text="关闭" />
                <span class="inline-hint">关闭后首页不显示顶部橙色公告条</span>
              </el-form-item>
              <el-form-item label="顶部公告内容">
                <el-input
                  v-model="form.announcement_bar_text"
                  type="textarea"
                  :rows="2"
                  maxlength="500"
                  show-word-limit
                  placeholder="例如：公告：新品上市，欢迎品尝！"
                />
              </el-form-item>
              <el-form-item label="打开时弹窗公告">
                <el-switch v-model="form.announcement_popup_enabled" active-text="开启" inactive-text="关闭" />
                <span class="inline-hint">开启后，用户打开小程序进入首页时会弹出公告（需填写下方内容或顶部内容）</span>
              </el-form-item>
              <el-form-item label="弹窗公告内容">
                <el-input
                  v-model="form.announcement_popup_text"
                  type="textarea"
                  :rows="4"
                  maxlength="2000"
                  show-word-limit
                  placeholder="留空则使用「顶部公告内容」作为弹窗正文"
                />
              </el-form-item>
            </el-form>
          </div>

          <div v-show="activeSection === 'banner'" class="section-block">
            <div class="section-title">首页轮播图（建议 4:3 或 16:9）</div>
            <div class="banner-grid">
              <div class="banner-item" v-for="(url, idx) in form.banner_images" :key="idx">
                <el-image v-if="url" :src="url" fit="cover" class="preview-img" />
                <div v-else class="img-placeholder">未设置</div>
                <div class="banner-actions">
                  <el-upload action="#" :show-file-list="false" :http-request="(p) => uploadBanner(p, idx)" accept="image/*">
                    <el-button size="mini" class="btn-secondary">上传替换</el-button>
                  </el-upload>
                  <el-button size="mini" class="btn-secondary danger" @click="removeBanner(idx)">删除</el-button>
                </div>
              </div>
              <div class="banner-add">
                <el-upload action="#" :show-file-list="false" :http-request="uploadBannerNew" accept="image/*">
                  <el-button class="btn-secondary" :disabled="form.banner_images.length >= 8">
                    新增轮播图
                  </el-button>
                </el-upload>
                <div class="hint">最多 8 张，当前 {{ form.banner_images.length }} 张</div>
              </div>
            </div>
          </div>

          <div v-show="activeSection === 'footer'" class="section-block">
            <div class="section-title">首页底部图片（4:3）</div>
            <el-image v-if="form.footer_image" :src="form.footer_image" fit="cover" class="preview-footer" />
            <div v-else class="img-placeholder footer-placeholder">未设置底部图片</div>
            <div class="footer-actions">
              <el-upload action="#" :show-file-list="false" :http-request="uploadFooter" accept="image/*">
                <el-button class="btn-secondary">上传替换底图</el-button>
              </el-upload>
              <el-button class="btn-secondary danger" @click="form.footer_image = ''">清空</el-button>
            </div>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { formatApiError } from '../utils/apiError'

export default {
  name: 'MiniDecoration',
  data() {
    return {
      activeSection: 'announcement',
      saving: false,
      form: {
        banner_images: [],
        footer_image: '',
        announcement_bar_enabled: true,
        announcement_bar_text: '',
        announcement_popup_enabled: false,
        announcement_popup_text: ''
      }
    }
  },
  mounted() {
    this.loadConfig()
  },
  methods: {
    onSectionSelect(index) {
      this.activeSection = index
    },
    async loadConfig() {
      try {
        const resp = await this.$api.get('/admin/mini-decoration')
        if (resp && resp.code === 200 && resp.data) {
          const d = resp.data
          this.form.banner_images = Array.isArray(d.banner_images) ? d.banner_images : []
          this.form.footer_image = d.footer_image || ''
          this.form.announcement_bar_enabled = d.announcement_bar_enabled !== false
          this.form.announcement_bar_text = d.announcement_bar_text != null ? String(d.announcement_bar_text) : ''
          this.form.announcement_popup_enabled = !!d.announcement_popup_enabled
          this.form.announcement_popup_text = d.announcement_popup_text != null ? String(d.announcement_popup_text) : ''
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '加载小程序装修失败'))
      }
    },
    async uploadCommon(param) {
      if (!param || !param.file) return ''
      const formData = new FormData()
      formData.append('file', param.file)
      try {
        const resp = await this.$api.post('/admin/mini-decoration/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        if (resp && resp.code === 200 && resp.data && resp.data.url) {
          this.$message.success('上传成功')
          return resp.data.url
        }
        this.$message.error((resp && resp.message) || '上传失败')
        return ''
      } catch (e) {
        this.$message.error(formatApiError(e, '上传失败'))
        return ''
      } finally {
        if (param.onSuccess) param.onSuccess()
      }
    },
    async uploadBanner(param, idx) {
      const url = await this.uploadCommon(param)
      if (!url) return
      this.$set(this.form.banner_images, idx, url)
    },
    async uploadBannerNew(param) {
      if ((this.form.banner_images || []).length >= 8) {
        this.$message.warning('轮播图最多 8 张')
        if (param && param.onSuccess) param.onSuccess()
        return
      }
      const url = await this.uploadCommon(param)
      if (!url) return
      this.form.banner_images = [...this.form.banner_images, url]
    },
    async uploadFooter(param) {
      const url = await this.uploadCommon(param)
      if (!url) return
      this.form.footer_image = url
    },
    removeBanner(idx) {
      this.form.banner_images.splice(idx, 1)
      this.form.banner_images = [...this.form.banner_images]
    },
    async saveConfig() {
      this.saving = true
      try {
        const payload = {
          banner_images: (this.form.banner_images || []).filter(Boolean),
          footer_image: this.form.footer_image || '',
          announcement_bar_enabled: !!this.form.announcement_bar_enabled,
          announcement_bar_text: this.form.announcement_bar_text || '',
          announcement_popup_enabled: !!this.form.announcement_popup_enabled,
          announcement_popup_text: this.form.announcement_popup_text || ''
        }
        const resp = await this.$api.put('/admin/mini-decoration', payload)
        if (resp && resp.code === 200) {
          this.$message.success(resp.message || '保存成功')
          await this.loadConfig()
        } else {
          this.$message.error((resp && resp.message) || '保存失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '保存失败'))
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.mini-decoration-page {
  padding: 0;
}
.decoration-layout {
  display: flex;
  align-items: flex-start;
  gap: 24px;
}
.decoration-aside {
  flex: 0 0 228px;
  max-width: 260px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px 0 8px;
  background: #fafafa;
}
.aside-caption {
  padding: 0 16px 12px;
  font-size: 12px;
  color: #909399;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.decoration-side-menu {
  width: 100% !important;
  border-right: none !important;
}
.decoration-side-menu >>> .el-submenu__title,
.decoration-side-menu >>> .el-menu-item {
  height: 44px;
  line-height: 44px;
}
.submenu-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #303133;
}
.submenu-title i {
  font-size: 16px;
  color: #606266;
}
.decoration-main {
  flex: 1;
  min-width: 0;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.section-block {
  margin-bottom: 8px;
}
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
}
.section-hint {
  font-size: 12px;
  color: #909399;
  margin: 0 0 12px;
  line-height: 1.5;
}
.announce-form {
  max-width: 720px;
}
.inline-hint {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
}
.banner-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}
.banner-item {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 10px;
  background: #fff;
}
.preview-img {
  width: 100%;
  height: 130px;
  border-radius: 6px;
}
.img-placeholder {
  width: 100%;
  height: 130px;
  border-radius: 6px;
  background: #f5f7fa;
  color: #909399;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}
.banner-actions {
  margin-top: 10px;
  display: flex;
  gap: 8px;
}
.banner-add {
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
}
.hint {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
}
.preview-footer {
  width: 280px;
  height: 210px;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}
.footer-placeholder {
  width: 280px;
  height: 210px;
}
.footer-actions {
  margin-top: 12px;
  display: flex;
  gap: 8px;
}
</style>
